import type { Combatant } from "@lancer-vtt/domain";
import { setOwnerCommand, toggleGrantsCommand, grantsOf, assignmentStrandsUnit } from "../controlAssignment";
import type { EncounterDispatch } from "../StatusEditor";

/** Every slot in the game (GM + players), for the OWNER and GRANTS rows. */
export type ControlPanelSlot = { id: string; name: string; role: "gm" | "player" };

/**
 * The GM's **CONTROL panel** — who drives this unit, and who sees it. One component, two mounts:
 * the EDIT inspector ({@link EditInspectorBody}) and, as a collapsible band under the PLAY inspector
 * (`InspectorBody`), so a GM mid-fight grants from the window already open instead of flipping to
 * EDIT (`unit-control-assignment` G-D5).
 *
 * Two rows, because a re-home and a grant are different gestures on different fields:
 * - **OWNER** — a radio-style row of GM + every player slot. Exactly one, always. This is the
 * owner of record (`controllerSlotId`), the value attribution, `pcCountFor` party size, and deploy
 * inheritance all key on.
 * - **GRANTS** — one line per non-owner player slot: `[Name] CTRL EYES`. CTRL is a control grant
 * (the co-controller list); EYES is a visibility grant (`visibilityGrantSlotIds`,
 * `decisions/0179`). The name chip sets both on, or both off when both are on; CTRL and EYES
 * toggle independently. A grant never touches the owner or the side, so a granted hostile stays a
 * hostile, in the hostile bucket. `controllerSlotId: null` with grants is legal and useful: it is
 * RAW's player-nominated allied NPC (Core p.60).
 *
 * The owner's own line is omitted from GRANTS: the owner already controls and sees the unit, and
 * the reducer drops the owner from the co-list on every write, so a CTRL chip there would do
 * nothing.
 *
 * **The stranding warning** is keyed on the OWNER row only: a player owner of an `{NPC}`-side unit
 * activates it in the hostile bucket. It advises where the CLI's `--slot`-with-no-`--side` guard
 * refuses ({@link assignmentStrandsUnit}), because the GM may want exactly that. The fix flips the
 * side in the same `set_controller` command.
 */
export function ControlPanel({
  combatant: c,
  actorId,
  slots,
  dispatch,
}: {
  combatant: Combatant;
  /** `""` renders every chip disabled (the host's tools-disabled thread). */
  actorId: string;
  slots: ControlPanelSlot[];
  dispatch: EncounterDispatch;
}) {
  const owner = c.controllerSlotId ?? null;
  const players = slots.filter((s) => s.role === "player");
  const grantees = players.filter((s) => s.id !== owner);
  const stranded = assignmentStrandsUnit({ combatant: c, slotId: owner });
  const grant = (slotId: string, axis: "both" | "control" | "view") =>
    actorId && dispatch(toggleGrantsCommand({ actorId, combatant: c, slotId, axis }));

  return (
    <div className="npc-ctl-edit-field cw-control-assign">
      <span className="npc-ctl-label">Control</span>

      <div className="cw-control-row" role="group" aria-label="owner of record">
        <span className="cw-microlabel">OWNER</span>
        <button
          type="button"
          className={`cw-chip${owner === null ? " cw-chip-on" : ""}`}
          aria-pressed={owner === null}
          disabled={!actorId}
          title={`Hand ${c.name} back to the GM`}
          onClick={() => actorId && owner !== null && dispatch(setOwnerCommand({ actorId, combatantId: c.id, controllerSlotId: null }))}
        >
          GM
        </button>
        {players.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`cw-chip${owner === s.id ? " cw-chip-on" : ""}`}
            aria-pressed={owner === s.id}
            disabled={!actorId}
            title={`Make ${s.name} the owner of record for ${c.name}`}
            onClick={() => actorId && owner !== s.id && dispatch(setOwnerCommand({ actorId, combatantId: c.id, controllerSlotId: s.id }))}
          >
            {s.name}
          </button>
        ))}
      </div>

      <div className="cw-control-grants" role="group" aria-label="grants">
        <span className="cw-microlabel">GRANTS</span>
        {grantees.length === 0 ? (
          <span className="cw-microlabel cw-control-empty">NO OTHER PLAYER SLOTS</span>
        ) : (
          grantees.map((s) => {
            const has = grantsOf(c, s.id);
            return (
              <div key={s.id} className="cw-control-row" role="group" aria-label={`grants for ${s.name}`}>
                <button
                  type="button"
                  className={`cw-chip${has.control && has.view ? " cw-chip-on" : ""}`}
                  aria-pressed={has.control && has.view}
                  disabled={!actorId}
                  title={has.control && has.view ? `Revoke ${s.name}'s control and view of ${c.name}` : `Let ${s.name} drive and see ${c.name}`}
                  onClick={() => grant(s.id, "both")}
                >
                  {s.name}
                </button>
                <button
                  type="button"
                  className={`cw-chip${has.control ? " cw-chip-on" : ""}`}
                  aria-pressed={has.control}
                  disabled={!actorId}
                  title={has.control ? `Stop ${s.name} driving ${c.name}` : `Let ${s.name} drive ${c.name}`}
                  onClick={() => grant(s.id, "control")}
                >
                  CTRL
                </button>
                <button
                  type="button"
                  className={`cw-chip${has.view ? " cw-chip-on" : ""}`}
                  aria-pressed={has.view}
                  disabled={!actorId}
                  title={has.view ? `Hide ${c.name}'s sheet from ${s.name}` : `Show ${s.name} ${c.name}'s full sheet`}
                  onClick={() => grant(s.id, "view")}
                >
                  EYES
                </button>
              </div>
            );
          })
        )}
      </div>

      {stranded && (
        <div className="cw-control-warn">
          <span className="cw-microlabel">
            {c.name} is {"{NPC}"}-side — its owner still activates it in the HOSTILE bucket. To let a player drive it on the
            hostile turn, keep the GM as owner and use GRANTS.
          </span>
          <button
            type="button"
            className="cw-chip"
            disabled={!actorId}
            title={`Flip ${c.name} to the player side`}
            onClick={() =>
              actorId && dispatch(setOwnerCommand({ actorId, combatantId: c.id, controllerSlotId: owner, allegiance: "pc" }))
            }
          >
            MAKE PC-SIDE
          </button>
        </div>
      )}
    </div>
  );
}
